import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { makeDashboardApi } from 'services/DashboardApi/DashboardApi';
import {
  ITopPerformersRequest,
  ITopPerformersResponse,
  IPerformerEntry,
  ITableConfig,
  PerformerCategory,
  DEFAULT_TABLE_CONFIG,
  TopPerformersSortBy,
  TopPerformersSortDirection,
  ESalesMetricsGroupBy,
  EDashboardBookingStatus,
} from 'services/DashboardApi/types';

interface ITopPerformersFilters {
  startYear: number;
  startMonth: number;
  endYear: number;
  endMonth: number;
  dateType: ESalesMetricsGroupBy;
  status?: EDashboardBookingStatus[];
  companyCountry?: string[];
  company?: string[];
  destination?: string[];
  hotel?: string[];
  salesUserUuid?: string;
}

interface ITopPerformersData {
  topTAs: IPerformerEntry[];
  topTCs: IPerformerEntry[];
  topHotels: IPerformerEntry[];
  totalTAs: number;
  totalTCs: number;
  totalHotels: number;
  periodStart: string;
  periodEnd: string;
  lastUpdated: string;
}

interface IUseTopPerformersReturn {
  data: ITopPerformersData;
  tableConfigs: Record<PerformerCategory, ITableConfig>;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  updateCategory: (category: PerformerCategory, config: Partial<ITableConfig>) => Promise<void>;
  isUpdatingCategory: PerformerCategory | null;
}

const INITIAL_DATA: ITopPerformersData = {
  topTAs: [],
  topTCs: [],
  topHotels: [],
  totalTAs: 0,
  totalTCs: 0,
  totalHotels: 0,
  periodStart: '',
  periodEnd: '',
  lastUpdated: '',
};

const INITIAL_TABLE_CONFIGS: Record<PerformerCategory, ITableConfig> = {
  TA: DEFAULT_TABLE_CONFIG,
  TC: DEFAULT_TABLE_CONFIG,
  HOTEL: DEFAULT_TABLE_CONFIG,
};

const getCategoryDataKey = (
  category: PerformerCategory
): keyof Pick<ITopPerformersResponse, 'topTAs' | 'topTCs' | 'topHotels'> => {
  switch (category) {
    case 'TA':
      return 'topTAs';
    case 'TC':
      return 'topTCs';
    case 'HOTEL':
      return 'topHotels';
  }
};

const getCategoryTotalKey = (
  category: PerformerCategory
): keyof Pick<ITopPerformersResponse, 'totalTAs' | 'totalTCs' | 'totalHotels'> => {
  switch (category) {
    case 'TA':
      return 'totalTAs';
    case 'TC':
      return 'totalTCs';
    case 'HOTEL':
      return 'totalHotels';
  }
};

export const useTopPerformers = (filters: ITopPerformersFilters): IUseTopPerformersReturn => {
  // Local state to hold merged data from multiple requests
  const [data, setData] = useState<ITopPerformersData>(INITIAL_DATA);
  const [tableConfigs, setTableConfigs] = useState<Record<PerformerCategory, ITableConfig>>(INITIAL_TABLE_CONFIGS);
  const [isUpdatingCategory, setIsUpdatingCategory] = useState<PerformerCategory | null>(null);

  // Build request params for initial load (all categories)
  const initialRequestParams: ITopPerformersRequest = useMemo(
    () => ({
      startYear: filters.startYear,
      startMonth: filters.startMonth,
      endYear: filters.endYear,
      endMonth: filters.endMonth,
      category: 'all',
      sortBy: DEFAULT_TABLE_CONFIG.sortBy,
      sortDirection: DEFAULT_TABLE_CONFIG.sortDirection,
      limit: DEFAULT_TABLE_CONFIG.limit,
      dateType: filters.dateType,
      status: filters.status,
      companyCountry: filters.companyCountry,
      company: filters.company,
      destination: filters.destination,
      hotel: filters.hotel,
      salesUserUuid: filters.salesUserUuid,
    }),
    [filters]
  );

  // Initial load query - fetches all 3 categories
  const initialQuery = useQuery({
    queryKey: ['top-performers', 'all', filters],
    queryFn: async () => {
      const api = makeDashboardApi();
      const response = await api.getTopPerformers(initialRequestParams);
      return response.data;
    },
    staleTime: 30000,
  });

  // Reset local overrides + table configs when the initial query refetches
  // due to a filter change (not a single-category update).
  const prevDataUpdatedAt = useRef(initialQuery.dataUpdatedAt);
  useEffect(() => {
    if (initialQuery.dataUpdatedAt !== prevDataUpdatedAt.current) {
      prevDataUpdatedAt.current = initialQuery.dataUpdatedAt;
      if (!isUpdatingCategory) {
        setData(INITIAL_DATA);
        setTableConfigs(INITIAL_TABLE_CONFIGS);
      }
    }
  }, [initialQuery.dataUpdatedAt, isUpdatingCategory]);

  // Merge query data with local overrides from single-category updates.
  // Local data only exists after an updateCategory call; it gets reset on filter changes above.
  const mergedData = useMemo(() => {
    if (!initialQuery.data) return data;

    return {
      topTAs: data.topTAs.length > 0 ? data.topTAs : initialQuery.data.topTAs,
      topTCs: data.topTCs.length > 0 ? data.topTCs : initialQuery.data.topTCs,
      topHotels: data.topHotels.length > 0 ? data.topHotels : initialQuery.data.topHotels,
      totalTAs: data.totalTAs || initialQuery.data.totalTAs,
      totalTCs: data.totalTCs || initialQuery.data.totalTCs,
      totalHotels: data.totalHotels || initialQuery.data.totalHotels,
      periodStart: initialQuery.data.periodStart,
      periodEnd: initialQuery.data.periodEnd,
      lastUpdated: initialQuery.data.lastUpdated,
    };
  }, [initialQuery.data, data]);

  // Function to update a single category (sort change or expand/collapse)
  const updateCategory = useCallback(
    async (category: PerformerCategory, configUpdate: Partial<ITableConfig>) => {
      const currentConfig = tableConfigs[category];
      const newConfig: ITableConfig = {
        sortBy: configUpdate.sortBy ?? currentConfig.sortBy,
        sortDirection: configUpdate.sortDirection ?? currentConfig.sortDirection,
        limit: configUpdate.limit ?? currentConfig.limit,
      };

      // Update config immediately for optimistic UI
      setTableConfigs(prev => ({
        ...prev,
        [category]: newConfig,
      }));

      setIsUpdatingCategory(category);

      try {
        const api = makeDashboardApi();
        const response = await api.getTopPerformers({
          startYear: filters.startYear,
          startMonth: filters.startMonth,
          endYear: filters.endYear,
          endMonth: filters.endMonth,
          category,
          sortBy: newConfig.sortBy,
          sortDirection: newConfig.sortDirection,
          limit: newConfig.limit,
          dateType: filters.dateType,
          status: filters.status,
          companyCountry: filters.companyCountry,
          company: filters.company,
          destination: filters.destination,
          hotel: filters.hotel,
          salesUserUuid: filters.salesUserUuid,
        });

        // Merge: only update the requested category
        const dataKey = getCategoryDataKey(category);
        const totalKey = getCategoryTotalKey(category);
        setData(prev => ({
          ...prev,
          [dataKey]: response.data[dataKey],
          [totalKey]: response.data[totalKey],
          periodStart: response.data.periodStart,
          periodEnd: response.data.periodEnd,
          lastUpdated: response.data.lastUpdated,
        }));
      } catch (error) {
        // Revert config on error
        setTableConfigs(prev => ({
          ...prev,
          [category]: currentConfig,
        }));
        throw error;
      } finally {
        setIsUpdatingCategory(null);
      }
    },
    [filters, tableConfigs]
  );

  // When initial query refetches (due to filter change), reset local overrides
  // so we use the fresh data from the initial query
  const effectiveData = useMemo(() => {
    if (initialQuery.isFetching && !isUpdatingCategory) {
      // Initial query is refetching (filters changed), use query data directly
      return initialQuery.data
        ? {
            topTAs: initialQuery.data.topTAs,
            topTCs: initialQuery.data.topTCs,
            topHotels: initialQuery.data.topHotels,
            totalTAs: initialQuery.data.totalTAs,
            totalTCs: initialQuery.data.totalTCs,
            totalHotels: initialQuery.data.totalHotels,
            periodStart: initialQuery.data.periodStart,
            periodEnd: initialQuery.data.periodEnd,
            lastUpdated: initialQuery.data.lastUpdated,
          }
        : INITIAL_DATA;
    }
    return mergedData;
  }, [initialQuery.data, initialQuery.isFetching, isUpdatingCategory, mergedData]);

  return {
    data: effectiveData,
    tableConfigs,
    isLoading: initialQuery.isLoading,
    isError: initialQuery.isError,
    error: initialQuery.error,
    updateCategory,
    isUpdatingCategory,
  };
};
